import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GET_PROJECTS, GET_CLIENTS, GET_CATALOGS, GET_LABELS, CREATE_PROJECT, DELETE_PROJECTS } from '../graphql/queries';
import { Project, Catalog, Client } from '../types';
import {
  Plus,
  LayoutGrid,
  List,
  Calendar,
  BarChart2,
  Users,
  X,
  Filter,
  Trash2
} from 'lucide-react';


function Projects() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRole = user?.role ? user.role.toLowerCase() : '';
  const canManageProjects = userRole === 'admin' || userRole === 'project_manager';

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [statusFilter, setStatusFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    clientId: '',
    type: '',
    status: 'Backlog',
    priority: 'Medium',
    size: '',
    dueDate: ''
  });

  const { data, loading, refetch } = useQuery<{ projects: Project[] }>(GET_PROJECTS, {
    variables: {
      status: statusFilter || undefined,
      priority: priorityFilter || undefined,
      size: sizeFilter || undefined,
      type: typeFilter || undefined
    }
  });
  const { data: clientsData } = useQuery(GET_CLIENTS, { variables: { limit: 100 } });
  const { data: typesData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'projectType' } });
  const { data: statusesData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'projectStatus' } });
  const { data: prioritiesData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'priority' } });
  const { data: sizesData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'size' } });

  const [createProject] = useMutation(CREATE_PROJECT, {
    onCompleted: () => {
      setShowCreateModal(false);
      setFormData({
        name: '',
        description: '',
        clientId: '',
        type: '',
        status: 'Backlog',
        priority: 'Medium',
        size: '',
        dueDate: ''
      });
      refetch();
    }
  });

  const [deleteProjects] = useMutation(DELETE_PROJECTS, {
    onCompleted: () => {
      setSelectedIds([]);
      refetch();
    }
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createProject({
      variables: {
        input: {
          ...formData,
          clientId: formData.clientId || null,
          dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null
        }
      }
    });
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked && data?.projects) {
      setSelectedIds(data.projects.map((p: Project) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    e.stopPropagation();
    if (e.target.checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
    }
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} projects?`)) {
      deleteProjects({ variables: { ids: selectedIds } });
    }
  };

  const getStatusColor = (status: string) => {
    const found = statusesData?.catalogs?.find(
      c => c.name.toLowerCase() === status.toLowerCase()
    );
    return found?.color || '#64748B';
  };

  const getPriorityClass = (priority: string = 'Medium') => {
    const priorityMap: Record<string, string> = {
      'Urgent': 'priority-urgent',
      'High': 'priority-high',
      'Medium': 'priority-medium',
      'Low': 'priority-low'
    };
    return priorityMap[priority] || 'priority-medium';
  };

  const getTypeColor = (typeName: string) => {
    const found = typesData?.catalogs?.find(c => c.name === typeName);
    return found?.color || '#4f46e5';
  };

  const getSizeColor = (sizeName: string) => {
    const found = sizesData?.catalogs?.find(c => c.name === sizeName);
    return found?.color || '#4f46e5';
  };

  const getInitials = (name?: string) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const getAvatarColor = (name?: string) => {
    const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444'];
    const index = name?.charCodeAt(0) ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  const formatDate = (dateStr?: string) => {

    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Projects</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          {canManageProjects && selectedIds.length > 0 && (
            <button className="btn btn-danger" onClick={handleBulkDelete}>
              <Trash2 size={18} />
              Delete Selected ({selectedIds.length})
            </button>
          )}
          {canManageProjects && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} />
              Add Project
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        <div className="filters-bar">
          <button className="btn btn-secondary">
            <Filter size={16} />
            Filter
          </button>

          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {statusesData?.catalogs?.map(status => (
              <option key={status.id} value={status.name}>{status.name}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={sizeFilter}
            onChange={(e) => setSizeFilter(e.target.value)}
          >
            <option value="">All Sizes</option>
            {sizesData?.catalogs?.map(size => (
              <option key={size.id} value={size.name}>{size.name}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="">All Priorities</option>
            {prioritiesData?.catalogs?.map(priority => (
              <option key={priority.id} value={priority.name}>{priority.name}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            {typesData?.catalogs?.map(type => (
              <option key={type.id} value={type.name}>{type.name}</option>
            ))}
          </select>

          <div className="flex-spacer" />

          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List size={18} />
            </button>
          </div>
        </div>

        {viewMode === 'grid' ? (
          <div className="projects-grid">
            {data?.projects?.map((project) => (
              <div
                key={project.id}
                className="project-card"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <div className="project-card-header">
                  <div>
                    <span 
                      className="badge"
                      style={{
                        backgroundColor: `${getStatusColor(project.status)}15`,
                        color: getStatusColor(project.status),
                        border: `1px solid ${getStatusColor(project.status)}25`
                      }}
                    >
                      {project.status}
                    </span>
                  </div>
                </div>

                <h3>{project.name}</h3>
                <div className="project-pills-row" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '8px 0 12px 0' }} onClick={(e) => e.stopPropagation()}>
                  {project.client?.company && (
                    <span className="badge-pill">
                      {project.client.company}
                    </span>
                  )}
                  {project.type && (
                    <span className="badge-pill" style={{ 
                      '--pill-bg': `${getTypeColor(project.type)}1a`, 
                      '--pill-color': getTypeColor(project.type),
                      '--pill-border': `${getTypeColor(project.type)}33`
                    } as React.CSSProperties}>
                      {project.type}
                    </span>
                  )}
                  {project.size && (
                    <span className="badge-pill" style={{ 
                      '--pill-bg': `${getSizeColor(project.size)}1a`, 
                      '--pill-color': getSizeColor(project.size),
                      '--pill-border': `${getSizeColor(project.size)}33`
                    } as React.CSSProperties}>
                      Talla: {project.size}
                    </span>
                  )}
                </div>

                <div className="project-card-footer">
                  <div className="project-date">
                    <Calendar size={14} />
                    {formatDate(project.dueDate)}
                  </div>
                  <div className={`project-priority ${getPriorityClass(project.priority)}`}>
                    <BarChart2 size={14} />
                    {project.priority}
                  </div>
                </div>

                <div className="project-team" style={{ marginTop: '12px' }}>
                  <Users size={14} style={{ color: 'var(--text-muted)' }} />
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Team</span>
                  <div className="team-avatars">
                    {project.developers?.slice(0, 3).map((dev: any, idx: number) => (
                      <div
                        key={idx}
                        className="team-avatar"
                        style={{ background: getAvatarColor(dev.resource?.name) }}
                        title={dev.resource?.name}
                      >
                        {getInitials(dev.resource?.name)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {canManageProjects && (
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={!!data?.projects?.length && selectedIds.length === data.projects.length}
                        ref={input => {
                          if (input) {
                            input.indeterminate = selectedIds.length > 0 && selectedIds.length < (data?.projects?.length || 0);
                          }
                        }}
                      />
                    </th>
                  )}
                  <th>Project</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Due Date</th>
                  <th>Progress</th>
                </tr>
              </thead>
              <tbody>
                {data?.projects?.map((project) => (
                  <tr
                    key={project.id}
                    onClick={() => navigate(`/projects/${project.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    {canManageProjects && (
                      <td onClick={(e) => e.stopPropagation()} style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(project.id)}
                          onChange={(e) => handleSelectOne(e, project.id)}
                        />
                      </td>
                    )}
                    <td>
                      <strong>{project.name}</strong>
                      <br />
                      <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                        {project.type}
                      </span>
                    </td>
                    <td>{project.client?.company || '-'}</td>
                    <td>
                      <span 
                        className="badge"
                        style={{
                          backgroundColor: `${getStatusColor(project.status)}15`,
                          color: getStatusColor(project.status),
                          border: `1px solid ${getStatusColor(project.status)}25`
                        }}
                      >
                        {project.status}
                      </span>
                    </td>
                    <td className={getPriorityClass(project.priority)}>
                      {project.priority}
                    </td>
                    <td>{formatDate(project.dueDate)}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div className="progress-bar" style={{ width: '80px' }}>
                          <div
                            className="progress-fill"
                            style={{ width: `${project.progress || 0}%` }}
                          />
                        </div>
                        <span style={{ fontSize: '13px' }}>{project.progress || 0}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(!data?.projects || data.projects.length === 0) && (
          <div className="empty-state">
            <LayoutGrid size={64} />
            <h3>No projects found</h3>
            <p>Create your first project to get started</p>
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Project</h2>
              <button className="btn-icon" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Project Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Client</label>
                    <select
                      className="form-select"
                      value={formData.clientId}
                      onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                    >
                      <option value="">Select client</option>
                      {clientsData?.clients?.clients?.map((client: Client) => (
                        <option key={client.id} value={client.id}>{client.company}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select
                      className="form-select"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      <option value="">Select type</option>
                      {typesData?.catalogs?.map(type => (
                        <option key={type.id} value={type.name}>{type.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      {statusesData?.catalogs?.map(status => (
                        <option key={status.id} value={status.name}>{status.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Priority</label>
                    <select
                      className="form-select"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    >
                      {prioritiesData?.catalogs?.map(priority => (
                        <option key={priority.id} value={priority.name}>{priority.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Size</label>
                    <select
                      className="form-select"
                      value={formData.size}
                      onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                    >
                      <option value="">Select size</option>
                      {sizesData?.catalogs?.map(size => (
                        <option key={size.id} value={size.name}>{size.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Due Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Projects;
